
import { defineComponent, PropType } from 'vue'
import { MetricType, StatType, ThresholdType } from '~/types/metric'
import { Metric, MetricNamespaceTrend } from '~/types/types'
import { formatDate, parseISODate } from '~/utils/date'
import { roundToSignificantNumber } from '~/utils/number'
import { formatIntl } from '~/utils/string'
import { TrendStatProps } from './TrendStat.vue'

interface TrendStatPropsT extends TrendStatProps {
  key: string
  show: boolean
  on: unknown
}

export default defineComponent({
  name: 'TrendSection',
  props: {
    dateRange: {
      type: String,
      default: '7d'
    },
    chartType: {
      type: String,
      default: 'line'
    },
    clip: {
      type: Boolean,
      default: false
    },
    namespacesTrend: {
      type: Object as PropType<MetricNamespaceTrend>,
      required: true
    },
    metricMeta: {
      type: Object as PropType<
        Pick<
          Metric,
          | 'shortcode'
          | 'name'
          | 'description'
          | 'supportsAggregateThreshold'
          | 'unit'
          | 'newCodeMetricShortcode'
        >
      >,
      required: true
    },
    canModifyThreshold: {
      type: Boolean,
      default: false
    },
    dataLoading: {
      type: Boolean,
      default: false
    },
    areSettingsOverridden: {
      type: Boolean,
      default: false
    },
    repoGroupName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      STAT_TYPE: StatType,
      AGGREGATE_METRIC_KEY: MetricType.aggregate,
      METRICS_DOC: 'https://docs.deepsource.com/docs/repository-view-metrics'
    }
  },
  computed: {
    trendStats(): Array<TrendStatPropsT | { type: 'blocker' }> {
      const stats: Array<TrendStatPropsT | { type: 'blocker' }> = [
        {
          key: StatType.metric,
          metric: this.metricTrendStat,
          type: StatType.metric,
          show: true,
          on: false
        },
        { type: 'blocker' },
        {
          key: StatType.threshold + ThresholdType.threshold,
          metric: {
            name: 'Overall threshold',
            shortcode: this.metricMeta.shortcode,
            ...this.thresholdTrendStat
          },
          type: StatType.threshold,
          canModifyThreshold: this.canModifyThreshold,
          show: this.canViewThresholdModal,
          on: this.thresholdListeners(this.namespacesTrend.threshold, 'overall threshold')
        }
      ]

      if (this.newCodeThresholdTrendStat) {
        stats.push({
          key: StatType.threshold + ThresholdType.newCodeThreshold,
          metric: {
            name: 'New code threshold',
            shortcode: this.metricMeta.newCodeMetricShortcode as string,
            ...this.newCodeThresholdTrendStat
          },
          type: StatType.threshold,
          canModifyThreshold: this.canModifyThreshold,
          show: this.canViewThresholdModal,
          on: this.thresholdListeners(this.namespacesTrend.newCodeThreshold, 'new code threshold')
        })
      }

      return stats
    },
    trendStatsLength(): number {
      return this.trendStats.reduce(
        (total, trendStat) => (trendStat.type !== 'blocker' && trendStat.show ? total + 1 : total),
        0
      )
    },
    isAggregate(): boolean {
      return this.namespacesTrend.key === this.AGGREGATE_METRIC_KEY
    },
    displayName(): string {
      return this.isAggregate ? 'Overall aggregate' : (this.metricMeta.name ?? '')
    },
    metricTrendStat(): { name: string; value: string } {
      return {
        name: this.displayName,
        value: this.namespacesTrend.valueDisplay ?? ''
      }
    },
    chartUpdateKey(): string {
      return (
        this.chartColors[0] +
        this.namespacesTrend.valueTrend.values.length +
        this.namespacesTrend.threshold
      )
    },
    thresholdTrendStat(): { value: number | null; unit: string | undefined } {
      return {
        value: Number(this.namespacesTrend.threshold),
        unit: this.metricMeta.unit?.toString()
      }
    },
    newCodeThresholdTrendStat(): { value: number | null; unit: string | undefined } | null {
      return this.metricMeta.newCodeMetricShortcode
        ? {
            value: Number(this.namespacesTrend.newCodeThreshold),
            unit: this.metricMeta.unit?.toString()
          }
        : null
    },
    graphData(): Record<string, Array<string | number | unknown> | undefined> {
      return {
        labels: this.namespacesTrend.valueTrend.labels.map((label: string | number) =>
          formatDate(parseISODate(label))
        ),
        dataSets: [
          {
            name: this.displayName,
            values: this.namespacesTrend.valueTrend.values
          }
        ],
        yMarkers:
          this.namespacesTrend.threshold || this.namespacesTrend.threshold === 0
            ? [
                {
                  label: 'Overall threshold',
                  value: this.namespacesTrend.threshold,
                  options: { labelPos: 'right', stroke: 'vanilla-400', lineType: 'dashed' }
                }
              ]
            : []
      }
    },
    chartColors(): string[] {
      return this.namespacesTrend.threshold || this.namespacesTrend.threshold === 0
        ? this.namespacesTrend.isPassing
          ? ['juniper-500']
          : ['cherry-500']
        : ['robin-500']
    },
    isUnitPercentage(): boolean {
      return Boolean(this.metricMeta.unit)
    },
    maxDigitInGraph(): number {
      return Math.max(...this.namespacesTrend.valueTrend.values)
    },
    maxClip(): number {
      if (this.isUnitPercentage) {
        return 100
      }

      return this.chartType === 'line' ? this.maxLineClip : this.maxBarClip
    },
    maxLineClip(): number {
      const exaggeratedMax = this.maxDigitInGraph * 1.25
      return roundToSignificantNumber(exaggeratedMax, exaggeratedMax.toString().length - 1)
    },
    maxBarClip(): number {
      return roundToSignificantNumber(
        this.maxDigitInGraph,
        this.maxDigitInGraph.toString().length - 1
      )
    },
    canViewThresholdModal(): boolean {
      return Boolean(
        (this.thresholdTrendStat.value ||
          this.thresholdTrendStat.value === 0 ||
          this.canModifyThreshold) &&
        (!this.isAggregate || this.metricMeta.supportsAggregateThreshold)
      )
    }
  },
  methods: {
    formatIntl,
    thresholdListeners(
      threshold: MetricNamespaceTrend['threshold'] | MetricNamespaceTrend['newCodeThreshold'],
      thresholdName: string
    ) {
      return {
        ...this.$listeners,
        addThreshold: (shortcode: string) => {
          this.$emit('addThreshold', shortcode, threshold, this.namespacesTrend.key, thresholdName)
        },
        openUpdateThresholdModal: (shortcode: string) => {
          this.$emit(
            'openUpdateThresholdModal',
            shortcode,
            threshold,
            this.namespacesTrend.key,
            thresholdName
          )
        },
        deleteThreshold: (shortcode: string) => {
          this.$emit('deleteThreshold', shortcode, this.namespacesTrend.key)
        }
      }
    },
    updateFilter(newValue: string): void {
      this.$emit('update-filter', newValue)
    },
    isTrendStatPropsT(trend: TrendStatPropsT | { type: 'blocker' }): trend is TrendStatPropsT {
      return trend.type !== 'blocker'
    }
  }
})
